import React, { useMemo } from 'react';
import CatalogImage from '../../catalog/CatalogImage';
import { formatProductTitle } from '../../formatProductTitle';
import InstagramEmbed from '../InstagramEmbed';
import ProductShelfSection from '../ProductShelfSection';
import { HOME_COPY_DEFAULTS } from '../../content/pageCopyDefaults';
import { logSocialClick } from '../../storefront/storefrontEvents';

/** Instagram / social proof block on the home page. */
export default function HomeInstagramSection({ site, homeCopy = {}, allProducts = [] }) {
  const embedHtml = String(site.instagram_embed_html || '').trim();
  const instagramUrl = String(site.instagram_url || '').trim();

  const fallbackPosts = useMemo(() => {
    const seenImages = new Set();
    const out = [];
    for (const p of allProducts || []) {
      const image = String(p?.image || '').trim();
      if (!image || seenImages.has(image)) continue;
      seenImages.add(image);
      out.push({
        key: `${p.category}-${p.id}`,
        image,
        caption: formatProductTitle(p.name || ''),
      });
      if (out.length >= 6) break;
    }
    return out;
  }, [allProducts]);

  const buttonLabel = homeCopy.instagram_button || HOME_COPY_DEFAULTS.instagram_button;
  const useScrollTiles = !embedHtml && fallbackPosts.length > 0;

  return (
    <ProductShelfSection
      eyebrow={homeCopy.instagram_eyebrow || HOME_COPY_DEFAULTS.instagram_eyebrow}
      title={homeCopy.instagram_title || HOME_COPY_DEFAULTS.instagram_title}
      actionHref={instagramUrl || undefined}
      actionLabel={instagramUrl ? buttonLabel : ''}
      layout={useScrollTiles ? 'scroll' : 'grid'}
      tone="default"
      className="home-instagram-shelf"
      ariaLabel="Instagram gallery"
    >
      {embedHtml ? (
        <div className="home-instagram-shelf__embed product-shelf__cell product-shelf__cell--wide">
          <InstagramEmbed html={embedHtml} />
        </div>
      ) : fallbackPosts.length === 0 ? (
        <p className="home-instagram-shelf__empty">
          {homeCopy.instagram_empty || HOME_COPY_DEFAULTS.instagram_empty}
        </p>
      ) : (
        fallbackPosts.map((item) => {
          const TileTag = instagramUrl ? 'a' : 'div';
          const linkProps = instagramUrl
            ? {
                href: instagramUrl,
                target: '_blank',
                rel: 'noopener noreferrer',
                onClick: () => logSocialClick({ platform: 'instagram', targetUrl: instagramUrl }),
              }
            : {};
          return (
            <div className="product-shelf__cell" key={item.key}>
              <TileTag
                className="home-instagram-tile"
                aria-label={item.caption ? `View ${item.caption} on Instagram` : 'Instagram preview'}
                {...linkProps}
              >
                <CatalogImage src={item.image} alt={item.caption} className="home-instagram-tile__img" />
                <span className="home-instagram-tile__overlay" aria-hidden>
                  <span className="home-instagram-tile__icon">↗</span>
                </span>
              </TileTag>
            </div>
          );
        })
      )}
    </ProductShelfSection>
  );
}
